/**
 * SVG Path Utilities
 * Helper functions for SVG path manipulation
 */

/**
 * Scale SVG path data by given x and y factors
 * @param pathData - SVG path data string (e.g., "M 0 0 L 10 10 Z")
 * @param scaleX - Horizontal scale factor
 * @param scaleY - Vertical scale factor
 * @returns Scaled path data string
 */
export const scalePathData = (pathData: string, scaleX: number, scaleY: number): string => {
  // Parse path data and scale coordinates
  // Path format: "M x y L x y L x y ..."
  const pathRegex = /([MLHVCSQTAZ])([^MLHVCSQTAZ]*)/gi
  let scaledPath = ''

  let match
  while ((match = pathRegex.exec(pathData)) !== null) {
    const command = match[1]
    const coords = match[2].trim()

    if (!coords) {
      scaledPath += command
      continue
    }

    // Split coordinates by whitespace or comma
    const numbers = coords.split(/[\s,]+/).filter(n => n.length > 0).map(parseFloat)

    scaledPath += command

    // Scale coordinates based on command type
    switch (command.toUpperCase()) {
      case 'M': // moveto
      case 'L': // lineto
      case 'T': // smooth quadratic bezier curveto
        // (x, y) pairs
        for (let i = 0; i < numbers.length; i += 2) {
          scaledPath += ` ${numbers[i] * scaleX} ${numbers[i + 1] * scaleY}`
        }
        break

      case 'H': // horizontal lineto
        // x only
        for (let i = 0; i < numbers.length; i++) {
          scaledPath += ` ${numbers[i] * scaleX}`
        }
        break

      case 'V': // vertical lineto
        // y only
        for (let i = 0; i < numbers.length; i++) {
          scaledPath += ` ${numbers[i] * scaleY}`
        }
        break

      case 'C': // cubic bezier curveto
        // (x1, y1, x2, y2, x, y) tuples
        for (let i = 0; i < numbers.length; i += 6) {
          scaledPath += ` ${numbers[i] * scaleX} ${numbers[i + 1] * scaleY}`
          scaledPath += ` ${numbers[i + 2] * scaleX} ${numbers[i + 3] * scaleY}`
          scaledPath += ` ${numbers[i + 4] * scaleX} ${numbers[i + 5] * scaleY}`
        }
        break

      case 'S': // smooth cubic bezier curveto
      case 'Q': // quadratic bezier curveto
        // (x1, y1, x, y) tuples
        for (let i = 0; i < numbers.length; i += 4) {
          scaledPath += ` ${numbers[i] * scaleX} ${numbers[i + 1] * scaleY}`
          scaledPath += ` ${numbers[i + 2] * scaleX} ${numbers[i + 3] * scaleY}`
        }
        break

      case 'A': // elliptical arc
        // (rx, ry, x-axis-rotation, large-arc-flag, sweep-flag, x, y) tuples
        for (let i = 0; i < numbers.length; i += 7) {
          scaledPath += ` ${numbers[i] * scaleX} ${numbers[i + 1] * scaleY}`
          scaledPath += ` ${numbers[i + 2]}` // rotation (no scale)
          scaledPath += ` ${numbers[i + 3]} ${numbers[i + 4]}` // flags (no scale)
          scaledPath += ` ${numbers[i + 5] * scaleX} ${numbers[i + 6] * scaleY}`
        }
        break

      case 'Z': // closepath
        // No coordinates
        break

      default:
        // Unknown command, keep as is
        scaledPath += ` ${coords}`
    }
  }

  return scaledPath.trim()
}
